import React, { useContext } from 'react'
import formStyles from 'styles/form.module.scss'
import { Field } from 'react-final-form'
import Fields from 'components/common/Fields'
import SelectGroupedOptions from 'components/common/SelectGroupedOptions'
import Dropdown from 'react-dropdown'
import DeviceId from 'utils/detectDevice'
import {
  required,
  noSpaces,
  fieldHasErr,
  inputHasErr,
  noRepeatingCharacters,
  noSpecialCharacters,
  characterLimit,
  composeValidators,
} from 'utils/validations'
import { FormDataContext } from 'providers/FormData'
import {
  getCountryDropdownOptions,
  getMotherTongueDropdownOptions,
} from 'api/transformers'

function hasNumber(input) {
  return /\d/.test(input)
}

const dayOptions = Array.from({ length: 9 }, (_, i) => '0' + (i + 1))
  .map(num => String(num))
  .concat(Array.from({ length: 22 }, (_, i) => i + 10).map(num => String(num)))

const monthOptions = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const yearOptions = (gender, currentYear) => {
  let legalMarriageAge = gender === 'Male' ? 21 : 18
  let upperYearRange = Number(currentYear) - legalMarriageAge
  let lowerAgeRange = 1950
  let ageRange = upperYearRange - lowerAgeRange + 1
  return Array.from({ length: ageRange }, (_, i) => i + 1950)
    .map(num => String(num))
    .reverse()
}

const namesStringMap = {
  Self: 'Your Name',
  Daughter: "Your daughter's name",
  Son: "Your son's name",
  Sister: "Your sister's name",
  Brother: "Your brother's name",
  Friend: "Your friend's name",
  Relative: "Your relative's name",
}

const genderMap = {
  Daughter: 'Female',
  Son: 'Male',
  Sister: 'Female',
}

const genderPronounMap = {
  Male: 'His',
  Female: 'Her',
}

const genderPronounMapForLocation = {
  Male: 'Where does he live?',
  Female: 'Where does she live?',
}

const getPostedByString = postedBy => {
  return namesStringMap[postedBy]
}

const getGenderFromFields = (gender, postedby) => {
  if (gender === undefined || gender === '') {
    return genderMap[postedby]
  }
  return gender
}

const resetFieldsIfNumberInput = (firstName, lastName) => {
  let firstNameVal = firstName.input.value
  let lastNameVal = lastName.input.value

  if (hasNumber(firstNameVal)) {
    firstName.input.onChange('')
  }

  if (hasNumber(lastNameVal)) {
    lastName.input.onChange('')
  }
}

const Gender = props => {
  return (
    <Fields names={['gender', 'postedby']}>
      {({ gender, postedby }) => {
        let postedByValue = postedby.input.value.value
        let chosenGender = getGenderFromFields(
          gender.input.value,
          postedByValue
        )
        return props.children({
          gender: chosenGender,
          postedby: postedByValue,
        })
      }}
    </Fields>
  )
}

const FullName = () => {
  return (
    <>
      <Fields names={['postedby']}>
        {({ postedby }) => {
          return (
            <label className={formStyles.layerLable}>
              {getPostedByString(
                DeviceId.isMobile()
                  ? postedby.input.value
                  : postedby.input.value.value
              )}
            </label>
          )
        }}
      </Fields>
      <div className="form-row">
        <Field
          name="first_name"
          validate={composeValidators(
            required('first_name'),
            noSpaces('first_name'),
            characterLimit('First name', 20),
            noRepeatingCharacters,
            noSpecialCharacters
          )}
        >
          {({ input, meta }) => (
            <div className="col-6">
              <input
                data-testid="first_name"
                className={`${inputHasErr(meta) &&
                  formStyles.inputError} form-control ${
                  formStyles.formTextFreeField
                }`}
                type="text"
                {...input}
                placeholder="First Name"
              />
            </div>
          )}
        </Field>
        <Field
          name="last_name"
          validate={composeValidators(
            required('last_name'),
            noSpaces('last_name'),
            characterLimit('Last name', 20),
            noRepeatingCharacters,
            noSpecialCharacters
          )}
        >
          {({ input, meta }) => (
            <div className="col-6">
              <input
                data-testid="last_name"
                className={`${inputHasErr(meta) &&
                  formStyles.inputError} form-control ${
                  formStyles.formTextFreeField
                }`}
                type="text"
                {...input}
                placeholder="Last Name"
              />
            </div>
          )}
        </Field>
        <Fields names={['first_name', 'last_name']}>
          {/* eslint-disable-next-line camelcase */}
          {({ first_name, last_name }) => {
            resetFieldsIfNumberInput(first_name, last_name)
            const fullNameErr =
              fieldHasErr(first_name) || fieldHasErr(last_name)
            return fullNameErr ? (
              <p
                data-testid="name_error"
                className={`${formStyles.error} col-md-12`}
              >
                {first_name.meta.error || last_name.meta.error}
              </p>
            ) : null
          }}
        </Fields>
      </div>
    </>
  )
}

const LabelForDob = () => (
  <Gender>
    {({ gender, postedby }) => {
      let pronoun = postedby === 'Self' ? 'Your' : genderPronounMap[gender]
      return (
        <label className={`${formStyles.label} col-md-12`}>
          {pronoun} date of birth
        </label>
      )
    }}
  </Gender>
)

const DayField = () => {
  return (
    <Field name="day" validate={composeValidators(required('day'))}>
      {({ input, meta }) => (
        <div className="col-4">
          {DeviceId.isDesktop() && (
            <Dropdown
              controlClassName="day_selector"
              menuClassName="day_options"
              options={dayOptions}
              value={dayOptions[0]}
              placeholder={'Day'}
              className={`${inputHasErr(meta) && formStyles.inputError}`}
              {...input}
            />
          )}

          {DeviceId.isMobile() && (
            <select
              data-testid="day_selector"
              className={`form-control ${inputHasErr(meta) &&
                formStyles.inputError} ${formStyles.selectMobile}`}
              {...input}
            >
              <option value="" hidden>
                Day
              </option>
              {dayOptions.map(option => {
                return (
                  <option value={option} key={option}>
                    {option}
                  </option>
                )
              })}
            </select>
          )}
        </div>
      )}
    </Field>
  )
}

const MonthField = () => {
  return (
    <Field name="month" validate={composeValidators(required('month'))}>
      {({ input, meta }) => (
        <div className="col-4">
          {DeviceId.isDesktop() && (
            <Dropdown
              controlClassName="month_selector"
              menuClassName="month_options"
              options={monthOptions}
              value={monthOptions[1]}
              placeholder={'Month'}
              className={`${inputHasErr(meta) && formStyles.inputError}`}
              {...input}
            />
          )}

          {DeviceId.isMobile() && (
            <select
              data-testid="month_selector"
              className={`form-control ${inputHasErr(meta) &&
                formStyles.inputError} ${formStyles.selectMobile}`}
              {...input}
            >
              <option value="" hidden>
                Month
              </option>
              {monthOptions.map(option => {
                return (
                  <option value={option} key={option}>
                    {option}
                  </option>
                )
              })}
            </select>
          )}
        </div>
      )}
    </Field>
  )
}

const YearField = ({ gender }) => {
  return (
    <Field name="year" validate={composeValidators(required('year'))}>
      {({ input, meta }) => (
        <div className="col-4">
          {DeviceId.isDesktop() && (
            <Dropdown
              controlClassName="year_selector"
              menuClassName="year_options"
              options={yearOptions(
                gender.input.value,
                new Date().getFullYear()
              )}
              value={''}
              placeholder={'Year'}
              className={`${inputHasErr(meta) && formStyles.inputError}`}
              {...input}
            />
          )}

          {DeviceId.isMobile() && (
            <select
              data-testid="year_selector"
              className={`form-control ${inputHasErr(meta) &&
                formStyles.inputError} ${formStyles.selectMobile}`}
              {...input}
            >
              <option value="" hidden>
                Year
              </option>
              {yearOptions(gender.input.value, new Date().getFullYear()).map(
                option => {
                  return (
                    <option value={option} key={option}>
                      {option}
                    </option>
                  )
                }
              )}
            </select>
          )}
        </div>
      )}
    </Field>
  )
}

const DoBErrorField = ({ day, month, year }) => {
  const fullDateErr =
    fieldHasErr(day) || fieldHasErr(month) || fieldHasErr(year)
  const fullDateErrMsg = fullDateErr
    ? day.meta.error || month.meta.error || year.meta.error
    : ''

  return fullDateErrMsg ? (
    <p data-testid="dob_error" className={`${formStyles.error} col-md-12`}>
      {fullDateErrMsg}
    </p>
  ) : null
}

const DateOfBirth = () => {
  return (
    <div className="form-row">
      <LabelForDob />
      <Fields names={['gender', 'day', 'month', 'year']}>
        {props => {
          return (
            <>
              <DayField {...props} />
              <MonthField {...props} />
              <YearField {...props} />
              <DoBErrorField {...props} />
            </>
          )
        }}
      </Fields>
    </div>
  )
}

const Religion = ({ domainReligion }) => {
  const { formData, loading } = useContext(FormDataContext)
  const religionOptions = formData.religions
  return (
    <div className="form-row">
      <label className={`${formStyles.label} col-md-12`}>Religion</label>
      <Field
        name="community"
        validate={composeValidators(required('community'))}
      >
        {({ input, meta }) =>
          !loading && (
            <div className="col-md-12">
              {DeviceId.isMobile() && (
                <select
                  data-testid="community_selector"
                  className={`form-control ${inputHasErr(meta) &&
                    formStyles.inputError} ${formStyles.selectMobile}`}
                  {...input}
                  disabled={domainReligion !== 'Select'}
                >
                  <option value={domainReligion} hidden>
                    {domainReligion}
                  </option>
                  {religionOptions.map((option, index) => {
                    return (
                      <option value={option.value} key={index}>
                        {option.label}
                      </option>
                    )
                  })}
                </select>
              )}

              {DeviceId.isDesktop() && (
                <Dropdown
                  controlClassName="community_selector"
                  menuClassName="community_options"
                  options={religionOptions}
                  value={domainReligion}
                  placeholder={domainReligion}
                  className={`${inputHasErr(meta) && formStyles.inputError}`}
                  disabled={domainReligion !== 'Select'}
                  {...input}
                />
              )}

              {meta.touched && meta.error && (
                <span
                  data-testid="community_error"
                  className={formStyles.error}
                >
                  {meta.error}
                </span>
              )}
            </div>
          )
        }
      </Field>
    </div>
  )
}

const MotherTongue = ({ domainLanguage }) => {
  const { formData, loading } = useContext(FormDataContext)
  const motherTongueOptions = getMotherTongueDropdownOptions(
    formData.motherTongues
  )
  return (
    !loading && (
      <div className="form-row">
        <label className={`${formStyles.label} col-md-12`}>Mother tongue</label>
        <Field
          name="mother_tongue"
          validate={composeValidators(required('mother_tongue'))}
        >
          {({ input, meta }) => (
            <div className="col-md-12">
              {DeviceId.isDesktop() && (
                <Dropdown
                  controlClassName="mother_tongue_selector"
                  menuClassName="mother_tongue_options"
                  options={motherTongueOptions}
                  value={domainLanguage}
                  placeholder={domainLanguage}
                  className={`${inputHasErr(meta) && formStyles.inputError}`}
                  disabled={domainLanguage !== 'Select'}
                  {...input}
                />
              )}
              {DeviceId.isMobile() && (
                <select
                  data-testid="mother_tongue_selector"
                  className={`form-control ${inputHasErr(meta) &&
                    formStyles.inputError} ${formStyles.selectMobile}`}
                  {...input}
                  disabled={domainLanguage !== 'Select'}
                >
                  <option value={domainLanguage} hidden>
                    {domainLanguage}
                  </option>
                  <SelectGroupedOptions optionsData={motherTongueOptions} />
                </select>
              )}
              {meta.touched && meta.error && (
                <span
                  data-testid="mother_tongue_error"
                  className={formStyles.error}
                >
                  {meta.error}
                </span>
              )}
            </div>
          )}
        </Field>
      </div>
    )
  )
}

const Location = ({ domainCountry }) => {
  const { formData, loading } = useContext(FormDataContext)
  const countryOptions = getCountryDropdownOptions(formData.countries)

  return (
    !loading && (
      <div className="form-row">
        <Gender>
          {({ gender, postedby }) => {
            let locationLabel =
              postedby === 'Self'
                ? 'Where do you live?'
                : genderPronounMapForLocation[gender]
            return (
              <label className={`${formStyles.label} col-md-12`}>
                {locationLabel}
              </label>
            )
          }}
        </Gender>

        <Field
          name="countryofresidence"
          validate={composeValidators(required('countryofresidence'))}
        >
          {({ input, meta }) => (
            <div className="col-md-12">
              {DeviceId.isDesktop() && (
                <Dropdown
                  controlClassName="countryofresidence_selector"
                  menuClassName="countryofresidence_options"
                  options={countryOptions}
                  value={domainCountry}
                  placeholder={domainCountry}
                  className={`${inputHasErr(meta) && formStyles.inputError}`}
                  {...input}
                />
              )}

              {DeviceId.isMobile() && (
                <select
                  data-testid="countryofresidence_selector"
                  className={`form-control ${inputHasErr(meta) &&
                    formStyles.inputError} ${formStyles.selectMobile}`}
                  {...input}
                >
                  <SelectGroupedOptions optionsData={countryOptions} />
                </select>
              )}
              {meta.touched && meta.error && (
                <span
                  data-testid="countryofresidence_error"
                  className={formStyles.error}
                >
                  {meta.error}
                </span>
              )}
            </div>
          )}
        </Field>
      </div>
    )
  )
}

const BasicDetails = props => {
  return (
    <>
      <h2 className={formStyles.formTitle}>Great! Now some basic details</h2>
      <FullName />
      <DateOfBirth />
      <Religion domainReligion={props.domainReligion} />
      <MotherTongue domainLanguage={props.domainLanguage} />
      <Location domainCountry={props.domainCountry} />
    </>
  )
}

export default BasicDetails
